import { remove, filter, omit } from 'lodash';

import {
  ContentInput,
  ClientProtectedFragment,
  Network,
  S3Object,
  UrlMetadataImage,
  UserProfileFragment,
  SharingChannelInfo,
} from 'api';
import { IVideoInput } from 'types/app';
import { isAdminUser, isContributorUser } from 'helpers';
import { AWS_REGION } from 'settings';

import {
  getVideo,
  hasBody,
  hasCommentary,
  hasDisclosures,
  hasGroup,
  hasPhoto,
  hasUrl,
  hasVideo,
  isTextOnly,
} from 'features/content/helpers';
import { EMPTY_SLATE_EDITOR } from '../../app-constants';

export const isOnlyModerator = (user: UserProfileFragment) => {
  if (isAdminUser(user)) {
    return false;
  }

  if (!user.group_moderator?.length) {
    return false;
  }

  return user.group_moderator.some(item => {
    const { added_by_user_id, user_id } = item;
    return added_by_user_id !== user_id;
  });
};

export const hasContributorPrivilege = (user: UserProfileFragment, moderatorMode: boolean) => {
  return !!(
    isAdminUser(user) ||
    isContributorUser(user) ||
    (isOnlyModerator(user) && moderatorMode)
  );
};

export const hasModeratorPrivilege = (user: UserProfileFragment, moderatorMode: boolean) => {
  return !!(isAdminUser(user) || (isOnlyModerator(user) && moderatorMode));
};

export const formatCommentary = (content: ContentInput) => {
  if (!hasCommentary(content)) {
    return null;
  }

  return remove(content.commentary, commentary => !!commentary.commentary);
};

export const formatDisclosures = (content: ContentInput) => {
  if (!hasDisclosures(content)) {
    return [];
  }

  const disclosures = filter(content.disclosures, disclosure => !!disclosure.disclosure_id);
  return disclosures.map(item => omit(item, 'disclosure'));
};

export const getDisclosureNetwork = (network: Network) => {
  switch (network) {
    case 'facebook': {
      return Network.Facebook;
    }
    case 'instagram': {
      return Network.Instagram;
    }
    case 'twitter': {
      return Network.Twitter;
    }
    case 'linkedin': {
      return Network.Linkedin;
    }
    default: {
      return null;
    }
  }
};

export const convertUrlImageToPhoto = (image: UrlMetadataImage): S3Object => {
  return {
    bucket: image.bucket,
    key: image.file_key,
    width: image.width,
    height: image.height,
    region: AWS_REGION,
  };
};

export const canBeShared = (client: ClientProtectedFragment, content: ContentInput) => {
  if (!client.allow_sharing) {
    return false;
  }

  if (client.public_post_page) {
    return true;
  }

  return !hasVideo(content) && !isTextOnly(content);
};

export const foundReadOnlyGroup = (
  groupIds: string[],
  groupsRules: Record<string, { read_only: boolean }>
) => {
  let readOnlyGroupFound = false;

  if (groupIds.length === 0) {
    return readOnlyGroupFound;
  }

  groupIds.forEach(id => {
    if (groupsRules[id]?.read_only) {
      readOnlyGroupFound = true;
    }
  });

  return readOnlyGroupFound;
};

export const getSafeBody = (content: ContentInput) => {
  const { body } = content.content_object;
  return body === EMPTY_SLATE_EDITOR ? null : body;
};

export const isValidPost = (content: ContentInput) => {
  const containsContent = !!(
    hasBody(content) ||
    hasUrl(content) ||
    hasPhoto(content) ||
    hasVideo(content)
  );

  const video = getVideo(content);
  const hasNoPendingUploads = !video || (video && !video.file);

  return containsContent && hasNoPendingUploads && hasGroup(content);
};

export const uploadVideo = async (video: IVideoInput, onProgress: (value: number) => void) => {
  const amplifyStorage = await import('@aws-amplify/storage');
  const Storage = amplifyStorage.default;

  Storage.configure({
    AWSS3: {
      bucket: video.bucket,
      region: video.region,
    },
  });

  await Storage.put(video.key, video.file, {
    contentType: video.mimeType,
    customPrefix: {
      public: '',
    },
    progressCallback(progress: ProgressEvent) {
      const { loaded, total } = progress;
      onProgress((loaded / total) * 100);
    },
  });
};

export const isEligibleForImmediateShare = (content: ContentInput) => {
  if (!isTextOnly(content)) {
    return true;
  }

  if (!hasVideo(content)) {
    return true;
  }

  return !content.content_id;
};

export const generateRepostChannelsString = (
  channels: SharingChannelInfo[],
  repostText: string
) => {
  let channelListString = '';
  channels.forEach(channel => {
    if (channelListString === '') {
      channelListString += `#${channel.channel_name}`;
    } else {
      channelListString += `, ${channel.channel_name}`;
    }
  });
  channelListString = `${repostText} ${channelListString}`;

  return channelListString;
};
